/**
 * Render an assigned template onto the mockup of the Woo product it is assigned to.
 *
 * A template stores one `mockup_attachment_id`, baked in the designer against the
 * catalog product it was authored on. Templates are product-agnostic, so that image
 * is the wrong garment for every other product. This module re-renders the composite
 * from the *target* product's own config, which is what the storefront then serves as
 * the WooCommerce product image.
 *
 * There is no PHP compositor — `useCanvasRenderer` is canvas-only — so the render has
 * to happen in the browser and be uploaded as a data URL.
 */
import { createPinia } from 'pinia';
import { useConfigStore, type ServerConfig } from '@designer/stores/configStore';
import { useDesignStore, type ViewName } from '@designer/stores/designStore';
import { useProductStore } from '@designer/stores/productStore';
import { useCanvasRenderer } from '@designer/composables/useCanvasRenderer';
import { useFonts } from '@designer/composables/useFonts';
import { useFontEmbedding } from '@designer/composables/useFontEmbedding';
import { canRehydrateSvgItem } from '@designer/composables/useDesignIO';
import { rehydrateSvgItem } from '@designer/composables/useSvgRecolor';
import { normalizeProductAttributes } from '@shared/productAttributes';
import { isLayerVisible } from '@core/imagePipeline';
import { soleEmbroideryPrintingCode } from '@core/embroideryProfile';
import { resolveViewAreaSizes, resolveViewPhysicalSizesCm } from '@designer/utils/printArea';
import { FIT_AREA_INSET, fitDesignItemsToPrintAreas, printSafetyMargins, readAuthoringAreaSizes } from '@core/templateAreaScale';
import { fitItemToBox, resolveItemBounds, resolveItemSvgExporter } from '@core/extensionRegistry';
import type { DesignItem } from '@core/types';
import { loadTemplateDesignerAssets } from './api';
import {
  acquireTemplateDesignerRuntime,
  releaseTemplateDesignerRuntime,
} from './templateDesignerRuntime';
import type { DesignerState, PublicDesignTemplate } from './types';

/** Shape returned by `GET /design-templates/quick-config`. */
export type QuickConfig = ServerConfig & {
  assignedTemplate: PublicDesignTemplate;
  advancedUrl?: string;
  replaceProductImage?: boolean;
  wooProductId?: number;
  productMockup?: ProductMockupState;
  designerAssets?: { js: string[]; css: string[] };
  /** Product-page selectors on OpenCart / PrestaShop; WooCommerce's are the defaults. */
  hostDom?: Partial<QuickHostDom>;
};

export interface QuickHostDom {
  cartForm: string;
  cartButton: string;
  quantityInput: string;
  galleryImage: string;
  priceTargets: string[];
}

export const WOO_HOST_DOM: QuickHostDom = {
  cartForm: 'form.cart',
  cartButton: 'form.cart .single_add_to_cart_button',
  quantityInput: 'input.qty',
  galleryImage: '.woocommerce-product-gallery__wrapper > .woocommerce-product-gallery__image:first-child img.wp-post-image, .wp-block-woocommerce-product-image-gallery img.wp-post-image',
  priceTargets: [
    '.summary .price',
    'form.variations_form .woocommerce-variation-price .price',
    '.wp-block-woocommerce-product-price[data-is-descendent-of-single-product-template="true"] > .wc-block-components-product-price',
  ],
};

export interface ProductMockupState {
  mockup_attachment_id: number;
  mockup_url: string;
  /** Fingerprint of the template + catalog product the composite should match. */
  mockup_signature: string;
  /** Fingerprint captured when the stored composite was rendered. */
  stored_mockup_signature: string;
}

export interface RenderedMockup {
  dataUrl: string;
  width: number;
  height: number;
}

/** True when no composite exists, or it predates a template / catalog product edit. */
export function isProductMockupStale(state: ProductMockupState | undefined | null): boolean {
  if (!state) return true;
  if (state.mockup_attachment_id <= 0) return true;
  if (!state.mockup_signature) return false;
  return state.stored_mockup_signature !== state.mockup_signature;
}

/**
 * Populate the product store from a server config.
 *
 * `useCanvasRenderer` reads `product.areas` to place the design inside the print
 * area and `product.colors` to tint garment layers, so a render without this
 * produces a bare, un-tinted garment with no artwork on it. The printing methods
 * matter just as much: the active method resolves the print-method pack, and
 * without one the composite drops embroidery stitching that the storefront
 * designer shows for the same product.
 */
export function initProductFromConfig(
  config: QuickConfig,
  product: ReturnType<typeof useProductStore>,
): void {
  const candidateViews = (config.items.views ?? []).map((view) => view.id);
  const views = candidateViews.length ? candidateViews : ['front', 'back', 'left', 'right'];
  const colors = config.items.design.map((entry) => ({
    hex: entry.color,
    title: entry.title,
    textureUrl: entry.texture || undefined,
    views: Object.fromEntries(views.map((view) => [view, entry[view] ?? ''])),
  }));
  product.setProduct({
    id: config.productId,
    title: config.product.title || '',
    description: config.product.description || '',
    shortDescription: config.product.shortDescription || '',
    sku: config.product.sku || '',
    image: config.product.image || '',
    price: Number(config.product.salePrice || config.product.price || 0),
    regularPrice: Number(config.product.regularPrice || config.product.price || 0),
    salePrice: Number(config.product.salePrice || 0),
    colors,
    attributes: normalizeProductAttributes(config.product.attributes),
    areas: config.items.area,
    params: config.items.params,
  });
  product.setAvailablePrintings(config.printingTypes ?? []);
  const embroideryCode = soleEmbroideryPrintingCode(
    config.printingTypes,
    config.items.embroideryProfile,
  );
  if (embroideryCode) product.setSelectedPrintingCode(embroideryCode);
}

/** Flatten onto white and cap the long edge, keeping product images light enough for the gallery. */
export function optimizedMockupDataUrl(source: HTMLCanvasElement, maxSide = 1200): string {
  const scale = Math.min(1, maxSide / Math.max(source.width, source.height));
  const output = document.createElement('canvas');
  output.width = Math.max(1, Math.round(source.width * scale));
  output.height = Math.max(1, Math.round(source.height * scale));
  const context = output.getContext('2d');
  if (!context) return source.toDataURL('image/jpeg', 0.88);
  context.fillStyle = '#fff';
  context.fillRect(0, 0, output.width, output.height);
  context.drawImage(source, 0, 0, output.width, output.height);
  return output.toDataURL('image/jpeg', 0.88);
}

/**
 * Fit template items into this product's print area.
 *
 * The storefront designer remaps the authoring print area onto the target one, so the
 * customer sees the placement the template author intended. A catalog thumbnail has the
 * opposite need: a chest-sized print is unreadable at 1200px on a garment, so here the
 * design's bounding box is contain-scaled to fill the whole design area instead.
 *
 * The measured bounds and the box handoff both come from the registry, so addon-owned
 * layouts report their real extent and are resized the way their resolver wants.
 *
 * A layer covering its authoring area (a full-bleed pattern) is a background, as in the
 * designer: it covers the target area plus its bleed instead of joining the fit, which
 * would otherwise shrink the whole design to the background's width.
 */
export function scaleTemplateStateToConfig(state: DesignerState, config: QuickConfig): DesignerState {
  const stageWidth = Math.max(1, Number(config.items.boxWidth) || 500);
  const stageHeight = Math.max(1, Number(config.items.boxHeight) || 500);
  const targetAreas = resolveViewAreaSizes(config.items.area, stageWidth, stageHeight);
  return {
    ...state,
    items: fitDesignItemsToPrintAreas(
      state.items as Record<string, DesignItem[]>,
      targetAreas,
      {
        inset: FIT_AREA_INSET,
        margins: printSafetyMargins(targetAreas, resolveViewPhysicalSizesCm(config.items.params), config.items.preflightProfile),
        sourceAreas: readAuthoringAreaSizes(state.metadata),
        measure: resolveItemBounds,
        fitToBox: fitItemToBox,
      },
    ) as DesignerState['items'],
  };
}

/** Recolored markup a saved state may have dropped on serialization. */
function needsSvgRehydration(item: DesignItem): boolean {
  if (!canRehydrateSvgItem(item)) return false;
  return !(typeof item.svg === 'string' && item.svg.includes('<svg'));
}

/**
 * Load every asset the layers need before anything measures them.
 *
 * Glyph outlines back both halves of this render: an addon resolver returns the box it
 * would draw only once its font is parsed, and the exported SVG needs the inlined face to
 * survive rasterization. Measuring first would fit the design against fallback geometry.
 *
 * Recolored cliparts need the same treatment: a state that kept only the source URL and
 * the color map renders in the artwork's original colors until the markup is rebuilt.
 */
async function prepareItemAssets(
  items: DesignItem[],
  pinia: ReturnType<typeof createPinia>,
): Promise<void> {
  await Promise.allSettled([
    useFonts(pinia).activateFontsForItems(items),
    useFontEmbedding(pinia).prefetchEmbeddableFonts(items),
    ...items.map((item) => resolveItemSvgExporter(item)?.prepare?.(item)),
    ...items.filter(needsSvgRehydration).map((item) => rehydrateSvgItem(item)),
  ]);
}

function firstViewWithLayers(
  state: DesignerState,
  design: ReturnType<typeof useDesignStore>,
): ViewName | null {
  const views = (state.views.length ? state.views : [state.activeView]) as ViewName[];
  const ordered = [state.activeView as ViewName, ...views];
  for (const view of ordered) {
    if ((design.items[view] || []).some((item) => isLayerVisible(item))) return view;
  }
  return null;
}

/**
 * Render the composite for one Woo product.
 *
 * Runs on its own Pinia instance (the pattern DesignTemplateDesignerView uses) so a
 * headless render never disturbs a designer or Quick Personalizer mounted on the page.
 *
 * @param colorIndex Catalog color to bake in; defaults to the product's first color.
 */
export async function renderAssignedTemplateMockup(
  quickConfig: QuickConfig,
  colorIndex = 0,
): Promise<RenderedMockup | null> {
  const pinia = createPinia();
  const config = useConfigStore(pinia);
  const design = useDesignStore(pinia);
  const product = useProductStore(pinia);
  const renderer = useCanvasRenderer(pinia);

  config.init(quickConfig as unknown as Record<string, unknown>);
  initProductFromConfig(quickConfig, product);

  // Text arcs, print-area shapes and mask images come from designer add-ons. They
  // register against the designer facade, so the runtime has to be in place before
  // they load — otherwise the composite silently drops those effects.
  await acquireTemplateDesignerRuntime(pinia);
  try {
    await loadTemplateDesignerAssets(quickConfig.designerAssets);

    const template = JSON.parse(
      JSON.stringify(quickConfig.assignedTemplate.designer_state),
    ) as DesignerState;

    // The designer does this via `useDesignIO.hydrateLoadedDesignAssets()`; this path
    // writes straight to the store and would skip it.
    await prepareItemAssets(Object.values(template.items).flat() as DesignItem[], pinia);

    const state = scaleTemplateStateToConfig(template, quickConfig);
    design.setAvailableViews(state.views as ViewName[]);
    design.loadDesignState({
      items: state.items as Parameters<typeof design.loadDesignState>[0]['items'],
      activeView: state.activeView,
      designId: null,
      designKey: '',
      designerId: '',
      designFile: '',
      origin: 'template',
    });

    const view = firstViewWithLayers(state, design);
    if (!view) return null;

    const canvas = await renderer.renderProductMockup(view, colorIndex);
    if (!canvas) return null;
    return {
      dataUrl: optimizedMockupDataUrl(canvas),
      width: canvas.width,
      height: canvas.height,
    };
  } finally {
    releaseTemplateDesignerRuntime();
  }
}
